import type { UseFormReturnType } from "@mantine/form";
import type {
  LegacyCaseDetail,
  LegacyExtractionSummary,
  LegacyPatientData,
  LegacyReviewData,
  LegacyReviewedSpecimen,
  LegacySaveReviewRequest,
  LegacySex,
} from "../types/legacyCases";
import { formatDateOnlyForDisplay, parseDateOnly } from "./dateOnly";
import { requiresLegacySourceAcknowledgment } from "./legacySourceComparison";
import {
  legacyPatientSourceChange,
  sameLegacyPatientIdentity,
  reviewedLegacyPatientCandidates,
  type LegacyPatientCandidate,
} from "./legacyPatientMapping";

export const LEGACY_NARRATIVES = [
  {
    field: "clinicalHistoryText",
    label: "Clinical history",
    kinds: ["clinicalHistory"],
  },
  { field: "diagnosisText", label: "Diagnosis", kinds: ["diagnosis"] },
  {
    field: "grossDescriptionText",
    label: "Gross description",
    kinds: ["grossDescription"],
  },
  {
    field: "microscopicDescriptionText",
    label: "Microscopic description",
    kinds: ["microscopicDescription"],
  },
  { field: "commentsText", label: "Comments", kinds: ["comment", "comments"] },
  {
    field: "historicalAddendaText",
    label: "Historical addenda and amendments",
    kinds: ["addendum", "amendment"],
  },
  {
    field: "signatoryText",
    label: "Original signatory text",
    kinds: ["signatory", "signature"],
  },
] as const;

export interface LegacyReviewValues {
  sourceRowId: string;
  reportDocumentId: string;
  extractionId: string;
  patientAction: "" | "match" | "create";
  patientId: string;
  patientReviewedIdentity?: LegacyPatientData;
  patientReviewedCandidateIds?: string[];
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: LegacySex | "";
  medicalRecordNumber: string;
  physicianAction: "match" | "unmatched";
  physicianId: string;
  dateReceived: string;
  dateCollected: string;
  reportedDate: string;
  clinicalHistoryText: string;
  diagnosisText: string;
  grossDescriptionText: string;
  microscopicDescriptionText: string;
  commentsText: string;
  historicalAddendaText: string;
  signatoryText: string;
  additionalSections: NonNullable<LegacyReviewData["additionalSections"]>;
  includeSpecimens: boolean;
  specimens: LegacyReviewedSpecimen[];
  conflictsAcknowledged: boolean;
}

export type LegacyReviewForm = UseFormReturnType<LegacyReviewValues>;
export type LegacyReportText = Pick<
  LegacyReviewValues,
  (typeof LEGACY_NARRATIVES)[number]["field"] | "additionalSections"
>;

export function legacyDraftReportText(
  values: LegacyReviewValues
): LegacyReportText {
  return {
    ...Object.fromEntries(
      LEGACY_NARRATIVES.map(({ field }) => [field, values[field]])
    ),
    additionalSections: values.additionalSections,
  } as LegacyReportText;
}

export function formatLegacyDate(value: string | undefined): string {
  const parsed = parseDateOnly(value);
  return parsed ? formatDateOnlyForDisplay(parsed) : value || "—";
}

export function createLegacyReviewValues(
  detail: LegacyCaseDetail
): LegacyReviewValues {
  const saved = detail.latestReview?.data;
  const source =
    detail.sourceRows.find((row) => row.id === saved?.sourceRowId)
      ?.normalized ?? detail;
  const reports = detail.documents.filter(
    (document) => document.kind === "report"
  );
  const patient =
    saved?.patient.action === "create" ? saved.patient : undefined;
  const values: LegacyReviewValues = {
    ...emptyLegacyNarratives(),
    ...saved,
    sourceRowId: saved?.sourceRowId ?? detail.sourceRows[0]?.id ?? "",
    reportDocumentId:
      saved?.reportDocumentId ?? (reports.length === 1 ? reports[0].id : ""),
    extractionId: saved?.extractionId ?? "",
    patientAction: saved?.patient.action ?? "",
    patientId: saved?.patient.action === "match" ? saved.patient.patientId : "",
    patientReviewedIdentity:
      saved?.patient.action === "match"
        ? saved.patient.reviewedIdentity
        : undefined,
    patientReviewedCandidateIds: patient?.reviewedCandidateIds,
    firstName: patient?.firstName ?? source.firstName,
    lastName: patient?.lastName ?? source.lastName,
    dateOfBirth: patient?.dateOfBirth ?? source.dateOfBirth ?? "",
    sex: patient?.sex ?? normalizeLegacySex(source.sexRaw),
    medicalRecordNumber: patient?.medicalRecordNumber ?? "",
    physicianAction: saved?.physician.action ?? "unmatched",
    physicianId:
      saved?.physician.action === "match" ? saved.physician.physicianId : "",
    dateReceived: saved?.dateReceived ?? source.dateReceived ?? "",
    dateCollected: saved?.dateCollected ?? "",
    reportedDate: saved?.reportedDate ?? source.reportedDate ?? "",
    additionalSections: saved?.additionalSections ?? [],
    includeSpecimens: Boolean(saved?.specimens?.length),
    specimens: saved?.specimens ?? [],
    conflictsAcknowledged: saved?.conflictsAcknowledged ?? false,
  };
  const extractions = detail.extractions.filter(
    (entry) => entry.documentId === values.reportDocumentId
  );
  const extraction =
    extractions.find(
      (entry) => entry.result.parserVersion === detail.currentParserVersion
    ) ?? extractions[0];
  return !saved && extraction
    ? applyLegacyReportText(
        {
          ...values,
          medicalRecordNumber:
            singleLegacyReportValue(extraction, "medicalRecordNumber") || "",
          dateCollected: legacyReportDate(
            singleLegacyReportValue(extraction, "dateCollected")
          ),
        },
        extraction
      )
    : values;
}

function singleLegacyReportValue(
  extraction: LegacyExtractionSummary,
  field: string
): string | undefined {
  const values = Array.from(
    new Set(
      extraction.result.fields[field]?.map((value) => value.rawValue) || []
    )
  );
  return values.length === 1 ? values[0] : undefined;
}

function legacyReportDate(value?: string): string {
  if (!value) return "";
  const [date, ...time] = value.trim().split(/\s+/);
  if (time.length && !isLegacyReportTime(time.join(" "))) return "";
  const usDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(date);
  const normalized = usDate
    ? `${usDate[3]}-${usDate[1].padStart(2, "0")}-${usDate[2].padStart(2, "0")}`
    : date;
  return parseDateOnly(normalized) || "";
}

function isLegacyReportTime(value: string): boolean {
  return (
    /^(?:[01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value) ||
    /^(?:0?[1-9]|1[0-2]):[0-5]\d(?::[0-5]\d)?\s+[AP]M$/i.test(value)
  );
}

function emptyLegacyNarratives() {
  return {
    clinicalHistoryText: "",
    diagnosisText: "",
    grossDescriptionText: "",
    microscopicDescriptionText: "",
    commentsText: "",
    historicalAddendaText: "",
    signatoryText: "",
  };
}

function normalizeLegacySex(value?: string): LegacySex | "" {
  if (/^(m|male)$/i.test(value ?? "")) return "M";
  if (/^(f|female)$/i.test(value ?? "")) return "F";
  return value?.toLowerCase() === "other" ? "Other" : "";
}

export function applyLegacyReportText(
  values: LegacyReviewValues,
  extraction: LegacyExtractionSummary
): LegacyReviewValues {
  const updated = {
    ...values,
    ...legacyPatientSourceChange(values.extractionId, extraction.id),
    extractionId: extraction.id,
    conflictsAcknowledged: false,
  };
  LEGACY_NARRATIVES.forEach(({ field, kinds }) => {
    updated[field] = extraction.result.sections
      .filter((section) => (kinds as readonly string[]).includes(section.kind))
      .map((section) => section.text)
      .join("\n\n");
  });
  const mapped = LEGACY_NARRATIVES.flatMap(({ kinds }) => [...kinds]);
  updated.additionalSections = extraction.result.sections
    .filter(
      (section) => !mapped.includes(section.kind as (typeof mapped)[number])
    )
    .map(({ kind, label, text }) => ({ kind, label, text }));
  updated.additionalSections.push({
    kind: "sourceTranscript",
    label: "Complete report transcript",
    text: extraction.result.sourceText,
  });
  return updated;
}

export function buildLegacyReviewRequest(
  values: LegacyReviewValues,
  revision: number
): LegacySaveReviewRequest {
  const narrative = Object.fromEntries(
    LEGACY_NARRATIVES.map(({ field }) => [field, values[field] || undefined])
  );
  return {
    expectedRevision: revision,
    sourceRowId: values.sourceRowId,
    reportDocumentId: values.reportDocumentId,
    extractionId: values.extractionId || undefined,
    patient:
      values.patientAction === "match"
        ? {
            action: "match",
            patientId: values.patientId,
            reviewedIdentity: values.patientReviewedIdentity,
          }
        : {
            action: "create",
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth || undefined,
            sex: values.sex || undefined,
            medicalRecordNumber: values.medicalRecordNumber || undefined,
            reviewedCandidateIds: values.patientReviewedCandidateIds,
          },
    physician:
      values.physicianAction === "match"
        ? { action: "match", physicianId: values.physicianId }
        : { action: "unmatched" },
    dateReceived: values.dateReceived,
    dateCollected: values.dateCollected || undefined,
    reportedDate: values.reportedDate || undefined,
    ...narrative,
    additionalSections: values.additionalSections,
    specimens: values.includeSpecimens ? values.specimens : [],
    conflictsAcknowledged: values.conflictsAcknowledged,
  };
}

export function legacyOptionalDate(value: string): string | null {
  return value && !parseDateOnly(value) ? "Enter a valid date." : null;
}

export function validateLegacyImport(
  values: LegacyReviewValues,
  detail: LegacyCaseDetail,
  patientCandidates: LegacyPatientCandidate[] = detail.patientCandidates
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.patientAction)
    errors.patientAction = "Choose an existing patient or create a patient.";
  if (values.patientAction === "match" && !values.patientId)
    errors.patientId = "Select the matching patient.";
  if (values.patientAction === "match" && values.patientId) {
    const current =
      patientCandidates.find((patient) => patient.id === values.patientId) ??
      detail.patientCandidates.find(
        (patient) => patient.id === values.patientId
      );
    if (
      !values.patientReviewedIdentity ||
      (current &&
        !sameLegacyPatientIdentity(values.patientReviewedIdentity, current))
    )
      errors.patientId =
        "Compare the patient identity and choose Use this patient before importing.";
  }
  if (values.patientAction === "create")
    validateLegacyNewPatient(values, errors);
  if (
    values.patientAction === "create" &&
    patientCandidates.length &&
    !reviewedLegacyPatientCandidates(
      values.patientReviewedCandidateIds,
      patientCandidates
    )
  )
    errors.patientReviewedCandidateIds =
      "Review the existing patients and confirm that a new patient is needed.";
  if (values.physicianAction === "match" && !values.physicianId)
    errors.physicianId = "Select the referring physician.";
  if (!values.sourceRowId)
    errors.sourceRowId = "Select the source spreadsheet row.";
  if (!values.reportDocumentId)
    errors.reportDocumentId = "Select the authoritative final report.";
  if (!parseDateOnly(values.dateReceived))
    errors.dateReceived = "Enter the original received date.";
  if (
    requiresLegacySourceAcknowledgment(detail, values) &&
    !values.conflictsAcknowledged
  )
    errors.conflictsAcknowledged =
      "Review and acknowledge the source conflicts.";
  if (
    !values.extractionId &&
    detail.extractions.some(
      (extraction) => extraction.documentId === values.reportDocumentId
    )
  )
    errors.extractionId = "Select and review the available report extraction.";
  if (values.includeSpecimens) validateLegacySpecimens(values, detail, errors);
  return errors;
}

function validateLegacyNewPatient(
  values: LegacyReviewValues,
  errors: Record<string, string>
) {
  if (!values.firstName.trim())
    errors.firstName = "Enter the patient's first name.";
  if (!values.lastName.trim())
    errors.lastName = "Enter the patient's last name.";
  if (!parseDateOnly(values.dateOfBirth))
    errors.dateOfBirth = "Enter the patient's date of birth.";
  if (!values.sex) errors.sex = "Select the patient's sex.";
}

function validateLegacySpecimens(
  values: LegacyReviewValues,
  detail: LegacyCaseDetail,
  errors: Record<string, string>
) {
  const source =
    detail.extractions.find((entry) => entry.id === values.extractionId)?.result
      .sourceText ?? "";
  values.specimens.forEach((specimen, index) => {
    const path = `specimens.${index}`;
    if (
      !specimen.number.trim() ||
      values.specimens.some(
        (other, otherIndex) =>
          otherIndex !== index && other.number === specimen.number
      )
    )
      errors[`${path}.number`] = "Enter a unique specimen number.";
    if (
      !specimen.evidenceText.trim() ||
      !source.includes(specimen.evidenceText)
    )
      errors[`${path}.evidenceText`] =
        "Copy exact supporting text from the selected report extraction.";
    (specimen.completedSlides ?? []).forEach((slide, slideIndex, slides) => {
      if (
        !slide.number.trim() ||
        slides.some(
          (other, otherIndex) =>
            otherIndex !== slideIndex && other.number === slide.number
        )
      )
        errors[`${path}.completedSlides.${slideIndex}.number`] =
          "Enter a unique slide number.";
      if (!slide.evidenceText.trim())
        errors[`${path}.completedSlides.${slideIndex}.evidenceText`] =
          "Enter the evidence for this completed slide.";
    });
  });
}

export function focusLegacyFormError(
  form: LegacyReviewForm,
  errors: Record<string, unknown>
) {
  const first = Object.keys(errors)[0];
  const node = first ? form.getInputNode(first) : undefined;
  node?.scrollIntoView({ block: "center", behavior: "smooth" });
  node?.focus();
}
